import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { BlueprintTypes } from 'app/plugins/openstack/components/infrastructure/blueprint/model'
import { mergeLeft } from 'ramda'

export interface SessionState {
  unscopedToken: string
  scopedToken: string
  username: string
  expiresAt: string
  userDetails: { [key: string]: any }
  features: { [key: string]: any }
  isSsoToken: boolean
  onboardingRedirectToUrl: string
  activeRegion: string
  loadingRegion: boolean
  loadingTenant: boolean
  blueprintType: BlueprintTypes
  virtualNetworking: boolean
  underlayNetwork: {
    type: string
    label: string
    ranges: string
  }
  domain: {
    id: string
    name: string
  }
  tokenData: {
    expiresAt: string
    issuedAt: string
  }
  jwtToken: string
  activeTenant: string
  activeKaapiTenant: string
  kaapiTenantList: Array<string>
  isSingleRegionDeployment: boolean
  vmsLastPolledTimestamp: string
  // Pagination metadata for VMs
  vmsPagination?: {
    currentMarker?: string | null
    nextMarker?: string | null
    hasMore?: boolean
    pageSize?: number
    totalCount?: number
  }
  allVmsPagination?: {
    currentMarker?: string | null
    nextMarker?: string | null
    hasMore?: boolean
    pageSize?: number
    totalCount?: number
  }
  selfVmsPagination?: {
    currentMarker?: string | null
    nextMarker?: string | null
    hasMore?: boolean
    pageSize?: number
    totalCount?: number
  }
  // Page size preferences (persisted across sessions)
  vmsPageSize?: number
  hostVmsPageSize?: number
  // Search and sort preferences for VMs
  vmsSearchParams?: Record<string, string> | null
  vmsSortKey?: string
  vmsSortDir?: 'asc' | 'desc'
  // Cached params to track what was used to populate each cache
  vmsCachedParams?: {
    searchParams: Record<string, string> | null
    sortKey: string
    sortDir: string
    pageSize: number
  }
  allVmsCachedParams?: {
    searchParams: Record<string, string> | null
    sortKey: string
    sortDir: string
    pageSize: number
  }
  selfVmsCachedParams?: {
    searchParams: Record<string, string> | null
    sortKey: string
    sortDir: string
    pageSize: number
  }
  // Host-specific VM cache keys (for HostVmsListPage)
  hostVmsSearchParams?: Record<string, string> | null
  hostVmsSortKey?: string
  hostVmsSortDir?: 'asc' | 'desc'
  hostVmsCachedParams?: {
    searchParams: Record<string, string> | null
    sortKey: string
    sortDir: string
    pageSize: number
  }
  hostAllVmsCachedParams?: {
    searchParams: Record<string, string> | null
    sortKey: string
    sortDir: string
    pageSize: number
  }
  hostSelfVmsCachedParams?: {
    searchParams: Record<string, string> | null
    sortKey: string
    sortDir: string
    pageSize: number
  }
  // View preferences for VM list page toggles (shared across VirtualMachinesListPage and HostVmsListPage)
  vmsAllTenants?: boolean
  vmsAllOwners?: boolean
  // Track last visited VM list page to avoid unnecessary reloads
  lastVisitedVmPage?: 'vmList' | 'hostList' | null
  dex_client_secret: string
  showSessionExpiryWarning: boolean
  sessionExpiryWarningDismissed: boolean
}

export const initialState: SessionState = {
  unscopedToken: null,
  scopedToken: null,
  username: null,
  expiresAt: null,
  userDetails: {},
  features: {},
  isSsoToken: false,
  onboardingRedirectToUrl: null,
  activeRegion: null,
  loadingRegion: true,
  loadingTenant: false,
  virtualNetworking: null,
  blueprintType: null,
  underlayNetwork: null,
  domain: null,
  tokenData: {
    expiresAt: null,
    issuedAt: null,
  },
  jwtToken: null,
  activeTenant: null,
  activeKaapiTenant: null,
  kaapiTenantList: [],
  isSingleRegionDeployment: false,
  vmsLastPolledTimestamp: null,
  dex_client_secret: null,
  showSessionExpiryWarning: false,
  sessionExpiryWarningDismissed: false,
}

const { name: sessionStoreKey, reducer: sessionReducers, actions: sessionActions } = createSlice({
  name: 'session',
  initialState,
  reducers: {
    initSession: (state, { payload }: PayloadAction<Partial<SessionState>>) => {
      return mergeLeft(payload, initialState)
    },
    updateSession: (state, { payload }: PayloadAction<Partial<SessionState>>) => {
      return mergeLeft(payload, state)
    },
    destroySession: () => {
      return initialState
    },
  },
})

export { sessionStoreKey, sessionActions }
export default sessionReducers
